import { createContext, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";

export const SocketContext = createContext();

export function useWebsocket() {
  const websocket = useRef(null);

  function connect() {
    const url = import.meta.env.VITE_WEBSOCKET_URL;
    if (url === undefined) {
      // eslint-disable-next-line no-console
      console.warn("Websocket URL is not defined, not sending data");
      return null;
    }

    const socket = new WebSocket(url);
    socket.addEventListener("error", handleError);
    socket.addEventListener("close", connect, { once: true });
    websocket.current = socket;
  }

  function disconnect() {
    const socket = websocket.current;
    if (socket === null) return;

    socket.removeEventListener("error", handleError);
    socket.removeEventListener("close", connect, { once: true });
    socket.close();
  }

  function handleError(event) {
    Sentry.captureMessage(`Error in websocket: ${JSON.stringify(event)}`);
  }

  useEffect(() => {
    connect();

    return () => disconnect();
  }, []);

  function send(data) {
    const socket = websocket.current;
    // Any time we send an event, we check if the socket is ready and just log the result if not
    // See MDN for the readyState: https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState
    if (socket instanceof WebSocket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(data));
    } else {
      // eslint-disable-next-line no-console
      console.log("Websocket not available, message would be:", data);
    }
  }

  return { send };
}
